import { GetPathSegmentResult, IPath } from "./IPath";
import { PointHelper } from "./helpers/PointHelper";
import { PathHelper } from "./helpers/PathHelper";
import { Point } from "./Point";
import { IGetLinePartsStrategy } from "./IGetLinePartsStrategy";
import { Line } from "./Line";

/**
 * Represents a path in 2D space. The path is defined by a series of points.
 */
export class Path implements IPath {
  private _points: Point[] = [];
  private _distances: number[] = [];
  private _length: number = 0;

  public get points() {
    return this._points;
  }

  /**
   * The distances between the points. The distances are used to calculate the position of the elements on the line.
   */
  public get distances() {
    return this._distances;
  }

  public get length() {
    return this._length;
  }

  public get rotationAngle() {
    return PointHelper.getRotation(this.points[0], this.points[this.points.length - 1]);
  }

  constructor(points: Point[]) {
    this._points = points;
    this.initialize();
  }

  public toDataString(): string {
    return PointHelper.toDataString(this.points);
  }

  /**
   * Moves the path by the given x and y values and returns the new Path.
   */
  public move(x: number, y: number): Path {
    return new Path(
      this.points.map((p) => {
        return new Point(p.x + x, p.y + y);
      }),
    );
  }

  /**
   * Moves the path in the given direction by the given distance and returns the new Path.
   */
  public moveInDirection(distance: number, angle: number): Path {
    const [x, y] = PointHelper.getPointInDirection(distance, angle);
    return this.move(x, y);
  }

  /**
   * Splits the path into segments based on the given strategy. The segments are filtered based on the given filter.
   * @param strategy
   * @param filter
   * @returns
   *
   * @example
   * const path = new Path([
   *  { x: 0, y: 0 },
   *  { x: 10, y: 0 },
   *  { x: 10, y: 10 },
   *  { x: 15, y: 10 },
   * ]);
   * const strategy = new CutLineStrategy([5, 5]);
   * const result = path.getPathSegments(strategy);
   */
  public getPathSegments<T>(
    strategy: IGetLinePartsStrategy<T>,
    filter: ((context?: T) => boolean) | undefined = () => true,
  ): GetPathSegmentResult<T>[] {
    const linePartsResult = new Line(this._length).getLineParts(strategy);

    const pointsOnPath = linePartsResult.map(({ linePart }) => {
      const { start, end } = linePart;
      const startPoint: Point = PathHelper.getPointOnPath(this, start);
      const endPoint: Point = PathHelper.getPointOnPath(this, end);

      // Get the points between the start and end point
      const intermediatePoints = this.distances
        .map((distance, index) => (distance > start && distance < end ? index : undefined))
        .filter((i) => i !== undefined)
        .reduce<Point[]>((curr, index) => [...curr, this.points[index]], []);

      return new Path([startPoint, ...intermediatePoints, endPoint]);
    });

    // Filter the points based on the filter function and create the result.
    const result = pointsOnPath.reduce<GetPathSegmentResult<T>[]>((prev, curr, index) => {
      const context = linePartsResult[index].context;
      const isResult = filter(context);
      if (isResult) {
        const linePart = linePartsResult[index].linePart;
        return [...prev, { path: curr, linePart, context }];
      }
      return prev;
    }, []);

    return result;
  }

  private initialize() {
    const { distances, totalDistance } = PointHelper.calculateDistancesBetweenPoints(this.points);
    this._distances = distances;
    this._length = totalDistance;
  }
}
